import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
	IconArrowRotateClockwiseOutline18 as Refresh,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type { ToolAccess, ToolPolicies } from "@productlane/lib"
import {
	humanizeToolName,
	isGuardingPolicy,
	isReadOnlyTool,
	looksSignedOut,
	MAX_MCP_TOOLS_PER_SERVER,
	sharedToolAccess,
	toolAccessOf,
} from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Badge, Button, Dot, Empty, IconButton, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { RefsState, SecureRefOption } from "./tool-policy"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useZero } from "@/lib/zero"
import {
	SETTINGS_CARD_CLASS,
	SETTINGS_INSET_CLASS,
} from "../settings-switches/settings-container"
import {
	AccessControl,
	GROUP_ACCESS_OPTIONS,
} from "./connectors/access-control"
import { formatToolDescription, McpToolPolicySheet } from "./tool-policy"

function toolCapReached() {
	toast.info({
		title: `The agent can use up to ${MAX_MCP_TOOLS_PER_SERVER} tools per server`,
		description: "Turn one off to make room for another.",
		icon: SquaresConnected,
	})
}

interface Props {
	sectionId: string
	connectorTitle?: string
	allowedMcpTools: readonly string[]
	blockedInternalMcpTools: readonly string[]
	toolPolicies?: ToolPolicies | null
	refreshToken?: number
}

interface McpTool {
	name: string
	description: string | null
	inputSchema?: unknown
	schemaOmitted?: boolean
	readOnly?: boolean
}

function firstLine(description: string | null): string {
	return formatToolDescription(description).split("\n")[0] ?? ""
}

// Cards here sit straight in the page, so they bleed like SettingsContainer does.
const CARD = cn(SETTINGS_CARD_CLASS, SETTINGS_INSET_CLASS)

export function McpToolAccessList({
	sectionId,
	connectorTitle = "This server",
	allowedMcpTools,
	blockedInternalMcpTools,
	toolPolicies,
	refreshToken = 0,
}: Props) {
	const zero = useZero()
	const listTools = api.customData.listMcpTools.useMutation()
	const listRefs = api.customData.listSecureRefs.useMutation()

	const [tools, setTools] = useState<McpTool[] | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [configuring, setConfiguring] = useState<string | null>(null)
	const [refOptions, setRefOptions] = useState<SecureRefOption[]>([])
	const [refsState, setRefsState] = useState<RefsState>("idle")
	const refsRequested = useRef(false)

	const load = useCallback(
		async (refresh: boolean) => {
			setError(null)
			try {
				const result = await listTools.mutateAsync({ sectionId, refresh })
				if (result.success) {
					setTools(result.tools)
					if (result.truncated) {
						toast.info({
							title: `Showing the first ${result.tools.length} tools`,
							description:
								"This server advertises more than we can list at once.",
							icon: SquaresConnected,
						})
					}
				} else {
					setTools(null)
					setError(result.error)
				}
			} catch {
				setTools(null)
				setError("Could not reach this MCP server. Try again.")
			}
		},
		[listTools, sectionId],
	)

	const mountedToken = useRef(refreshToken)

	useEffect(() => {
		void load(mountedToken.current > 0)
		// Load once per connector; refreshing is an explicit action.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sectionId])

	useEffect(() => {
		if (refreshToken === mountedToken.current) return
		void load(true)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshToken])

	// The catalog costs a round trip to every HTTP connector, so it is fetched once
	// when a policy is first opened rather than on mount.
	const loadRefs = useCallback(async () => {
		if (refsRequested.current) return
		refsRequested.current = true
		setRefsState("loading")
		try {
			const result = await listRefs.mutateAsync({ sectionId })
			setRefOptions([...result.widget, ...result.record, ...result.connector])
			setRefsState("ready")
			if (result.unreachable.length > 0) {
				toast.info({
					title: `Could not read data from ${result.unreachable.join(", ")}`,
					description:
						"You can still type a reference by hand; only the suggestions are missing.",
					icon: SquaresConnected,
				})
			}
		} catch {
			refsRequested.current = false
			setRefsState("error")
		}
	}, [listRefs, sectionId])

	const openPolicy = useCallback(
		(toolName: string) => {
			setConfiguring(toolName)
			void loadRefs()
		},
		[loadRefs],
	)

	const lists = useMemo(
		() => ({ allowedMcpTools, blockedInternalMcpTools }),
		[allowedMcpTools, blockedInternalMcpTools],
	)

	const missing = useMemo(() => {
		if (!tools) return []
		const listed = new Set(tools.map((tool) => tool.name))
		return [
			...new Set([...allowedMcpTools, ...blockedInternalMcpTools]),
		].filter((name) => !listed.has(name))
	}, [tools, allowedMcpTools, blockedInternalMcpTools])

	const groups = useMemo(() => {
		if (!tools) return []
		const lookups = tools.filter((tool) => isReadOnlyTool(tool))
		const changes = tools.filter((tool) => !isReadOnlyTool(tool))
		return [
			{ key: "lookups", title: "Look things up", tools: lookups },
			{ key: "changes", title: "Make changes", tools: changes },
		].filter((group) => group.tools.length > 0)
	}, [tools])

	const setAccess = useCallback(
		(names: readonly string[], access: ToolAccess) => {
			if (access === "agent") {
				const allowed = new Set([...lists.allowedMcpTools, ...names])
				if (allowed.size > MAX_MCP_TOOLS_PER_SERVER) {
					toolCapReached()
					return
				}
			}
			zero.mutate(
				mutators.sidebarSections.setToolAccess({
					id: sectionId,
					tools: [...names],
					access,
				}),
			)
			analytics.track("sidebar-section-mcp-tools-change")
		},
		[lists, sectionId, zero],
	)

	return (
		<div data-testid="mcp-tool-access-list">
			{listTools.isLoading && !tools ? (
				<div className={cn(CARD, "space-y-2 p-4")}>
					{[0, 1, 2].map((row) => (
						<div
							key={row}
							className="h-8 animate-pulse rounded bg-gray-100 dark:bg-gray-800"
						/>
					))}
				</div>
			) : null}

			{error ? (
				<div className={cn(CARD, "flex items-start gap-3 p-4")}>
					<div className="min-w-0 flex-1">
						<div className="text-sm font-medium">
							{looksSignedOut(error)
								? `${connectorTitle} rejected the saved API key`
								: `Couldn't reach ${connectorTitle}`}
						</div>
						<div className="mt-0.5 text-xs tracking-tight text-secondary">
							{looksSignedOut(error)
								? "Enter a new key below so the agent can use it again."
								: "Check the URL under Connection, then try again."}
						</div>
						<div className="mt-2 line-clamp-3 break-words text-xs text-tertiary">
							{error}
						</div>
					</div>
					<Button
						variant="outline"
						className="flex-none"
						onClick={() => void load(true)}
					>
						Try again
					</Button>
				</div>
			) : null}

			{tools && tools.length === 0 ? (
				<div className={cn(CARD, "p-4")}>
					<Empty
						variant="left"
						title="No tools yet"
						text="This server exposes no tools yet."
						action={
							<Button
								variant="outline"
								Icon={Refresh}
								disabled={listTools.isLoading}
								onClick={() => void load(true)}
							>
								Refresh
							</Button>
						}
					/>
				</div>
			) : null}

			<div className="space-y-3">
				{groups.map((group) => {
					const names = group.tools.map((tool) => tool.name)
					const shared = sharedToolAccess(names, lists)
					return (
						<div
							key={group.key}
							className={cn(
								CARD,
								"divide-y divide-gray-200 dark:divide-gray-700/80",
							)}
						>
							<div className="flex items-center gap-3 px-4 py-2.5">
								<span className="text-sm font-medium">{group.title}</span>
								<span className="text-xs text-tertiary">
									{group.tools.length}
								</span>
								<span className="flex-1" />
								<span
									className={cn(
										"text-xs text-tertiary",
										shared !== null && "invisible",
									)}
								>
									Custom
								</span>
								<AccessControl
									name={`mcp-group-${sectionId}-${group.key}`}
									label={`Access for every ${group.title.toLowerCase()} action`}
									value={shared}
									options={GROUP_ACCESS_OPTIONS}
									onChange={(access) => setAccess(names, access)}
								/>
							</div>
							{group.tools.map((tool) => {
								const policy = toolPolicies?.[tool.name] ?? null
								const guarded = isGuardingPolicy(policy)
								const summary = firstLine(tool.description)
								return (
									<div
										key={tool.name}
										className="flex items-center gap-3 px-4 py-2.5"
									>
										<div className="min-w-0 flex-1">
											<Tooltip label={tool.name} shouldWrapChildren>
												<span className="block truncate text-sm">
													{humanizeToolName(tool.name)}
												</span>
											</Tooltip>
											{summary && tool.description ? (
												<Tooltip
													label={
														<span className="block max-h-48 max-w-80 overflow-y-auto whitespace-pre-line">
															{formatToolDescription(tool.description)}
														</span>
													}
													shouldWrapChildren
												>
													<span className="mt-0.5 line-clamp-2 text-xs tracking-tight text-secondary">
														{summary}
													</span>
												</Tooltip>
											) : null}
										</div>
										<Tooltip
											label={
												guarded
													? "Some inputs are set by your rules, not the AI."
													: "The AI fills in every input."
											}
											shouldWrapChildren
										>
											<Button
												variant="ghost"
												size="xs"
												onClick={() => openPolicy(tool.name)}
											>
												<span
													data-guarded={guarded}
													className="flex items-center gap-1.5"
												>
													{guarded ? <Dot color="green" size="sm" /> : null}
													Rules
												</span>
											</Button>
										</Tooltip>
										<AccessControl
											name={`mcp-tool-${sectionId}-${tool.name}`}
											label={`Access for ${tool.name}`}
											value={toolAccessOf(tool.name, lists)}
											onChange={(access) => setAccess([tool.name], access)}
										/>
									</div>
								)
							})}
						</div>
					)
				})}
			</div>

			{configuring && tools
				? (() => {
						const tool = tools.find((entry) => entry.name === configuring)
						if (!tool) return null
						return (
							<McpToolPolicySheet
								open
								onOpenChange={(next) => !next && setConfiguring(null)}
								sectionId={sectionId}
								toolName={tool.name}
								toolDescription={tool.description}
								inputSchema={tool.inputSchema}
								schemaUnavailable={!!tool.schemaOmitted}
								policy={toolPolicies?.[tool.name] ?? null}
								refOptions={refOptions}
								refsState={refsState}
								onRetryRefs={() => {
									refsRequested.current = false
									void loadRefs()
								}}
							/>
						)
					})()
				: null}

			{missing.length > 0 ? (
				<div className={cn(CARD, "mt-3 p-4")}>
					<div className="flex items-center gap-1.5">
						<Badge variant="dark-gray">Not on the server</Badge>
						<span className="text-xs text-secondary">
							Saved, but the server no longer lists these.
						</span>
					</div>
					<div className="mt-2 space-y-1">
						{missing.map((name) => (
							<div key={name} className="flex items-center gap-1.5">
								<span className="text-xs text-secondary">{name}</span>
								<IconButton
									Icon={XIcon}
									variant="ghost"
									onClick={() => setAccess([name], "team")}
								/>
							</div>
						))}
					</div>
				</div>
			) : null}
		</div>
	)
}
